import { useCallback, useContext, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ContentButton from '../../../draft-content-transplant/ContentButton';
import contentOutput from '../../editorCapabilities/ContentOutputTypes';
import { RTEToolbarButton } from '../../components/StyledTextToolbarButton';
import { TextToolbarIcon } from 'rich-text-lib/components/editor/TextToolbarIcon';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import useDebouncedEditorStateContext from '../../contexts/useDebouncedEditorStateContext';
import hubHttpClient from 'hub-http/clients/apiClient';
import PortalIdParser from 'PortalIdParser';
import { insertContent } from '../../utils/documentUtils';
import { convertToPlaintextState } from '../../utils/plaintextUtils';
import identity from 'transmute/identity';
import GenericPopoverHeader from '../../components/GenericPopoverHeader';
import InsertContentDecoratorV2 from '../../../draft-transplant/InsertContentDecoratorV2';
import { templatesApiWrapper } from './TemplatesApi';
import TemplatesPopoverEmptyState, { getTemplatesUrl } from './TemplatesPopoverEmptyState';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import { getGlobalVars } from '../../utils/globalVarUtil';
import TemplatesPopoverErrorState from './TemplatesPopoverErrorState';
import { SalesTemplatesIcon } from 'foundations-assets/icons/components/SalesTemplatesIcon';

/** Metadata returned when a template is inserted into the editor */

/** Configuration for the Templates insert capability (saved email templates) */
import { jsx as _jsx } from "react/jsx-runtime";
const TemplatesButton = ({
  onClick
}) => /*#__PURE__*/_jsx(Tooltip, {
  title: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.tooltips.insertTemplates"
  }),
  children: /*#__PURE__*/_jsx(RTEToolbarButton, {
    onClick: onClick,
    selected: false,
    children: /*#__PURE__*/_jsx(TextToolbarIcon, {
      icon: SalesTemplatesIcon
    })
  })
});
export default function InsertTemplates({
  onInsertContent = identity,
  onOpenPopover,
  apiWrapper = templatesApiWrapper,
  apiClient = hubHttpClient,
  outputType = contentOutput.RENDERED,
  contactEmail,
  contactVid,
  supplementalObjects,
  renderedPopoverClassname,
  onInsertComplete = () => {},
  Header = GenericPopoverHeader,
  PopoverEmptyState = TemplatesPopoverEmptyState,
  PopoverErrorState = TemplatesPopoverErrorState
}) {
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const {
    usePlaintextMode,
    plaintextOptions
  } = getGlobalVars(useContext(EditorConfigContext));
  const insertTemplate = useCallback(({
    htmlBody,
    metadata
  }) => {
    const newState = insertContent({
      htmlBody,
      editorState,
      from: editorState.selection.from,
      to: editorState.selection.to
    });
    setEditorState(usePlaintextMode ? convertToPlaintextState(newState, plaintextOptions) : newState);
    onInsertComplete();
    onInsertContent(metadata);
  }, [editorState, setEditorState, usePlaintextMode, plaintextOptions, onInsertComplete, onInsertContent]);
  const apiFunctions = useMemo(() => {
    const api = apiWrapper(apiClient);
    if (api.fetchUnrenderedContent) {
      return api;
    }
    return Object.assign({}, api, {
      fetchUnrenderedContent: templatesApiWrapper(apiClient).fetchUnrenderedContent
    });
  }, [apiWrapper, apiClient]);
  const TemplatesPopover = useMemo(() => {
    const decoratorConfig = {
      outputType,
      apiFunctions,
      mergeTagOptions: {
        includeCustomTokens: true
      }
    };
    return InsertContentDecoratorV2(ContentButton({
      onInsertContent: identity,
      // @ts-expect-error ts-migrate FIXME
      portalId: PortalIdParser.get(),
      Button: TemplatesButton,
      PopoverErrorState,
      PopoverEmptyState,
      onOpenPopover,
      Header,
      renderedPopoverClassname,
      searchPlaceHolder: 'hubspot-prosemirror.generic.search.placeholder',
      search: apiFunctions.searchTemplates,
      popoverTestKey: 'templates-popover',
      manageUrl: getTemplatesUrl(),
      titleMessage: 'hubspot-prosemirror.templates.title'
    }), decoratorConfig);
  }, [onOpenPopover, outputType, apiFunctions, renderedPopoverClassname, PopoverEmptyState, PopoverErrorState, Header]);
  return /*#__PURE__*/_jsx(TemplatesPopover, {
    contactEmail: contactEmail,
    contactVid: contactVid,
    supplementalObjects: supplementalObjects,
    onChange: insertTemplate,
    useProsemirror: true
  });
}